import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/services/user/user.service';
import { PaymentOptionsService } from 'src/app/services/payment-options/payment-options.service';

@Component({
  selector: 'app-accounts-admin',
  templateUrl: './accounts-admin.component.html',
  styleUrls: ['./accounts-admin.component.css']
})
export class AccountsAdminComponent implements OnInit {
  // Datos de Payment Option actual
  paymentOptionId: string = '';
  paymentOption: any = null;
  
  // Datos de cuentas
  accountPayment: any;
  accountPaymentFiltered: any = []; // Cuentas filtradas
  paymentMethods: any = [];
  paymentMethodsSelected = null;
  accountPaymentOrigin: any = [];

  // Datos de payment methods (nivel superior)
  paymentMethodsList: any = [];
  paymentMethodsListFiltered: any = [];

  // Estados de carga
  accountPayment_load: any = true;
  paymentOption_load: boolean = true;
  paymentMethods_load: boolean = false;

  // Estado del colapso
  paymentMethodsCollapsed = true;

  // Filtros
  filters = {
    accountType: 'all', // all, specific payment method
    automaticVerification: 'all', // all, auto, manual
    clientVisibility: 'all', // all, visible, hidden
    bank: 'all' // all, specific bank
  };

  // Tipos de cuenta únicos
  uniqueAccountTypes: any[] = [];
  
  // Bancos únicos
  uniqueBanks: any[] = [];

  constructor(
    private _user: UserService,
    private paymentOptionsService: PaymentOptionsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Obtener el ID de payment option desde la ruta
    this.route.params.subscribe(params => {
      this.paymentOptionId = params['id'];
      if (this.paymentOptionId) {
        this.loadPaymentOptionData();
      } else {
        // Si no hay ID, redirigir a la lista principal
        this.router.navigate(['/console/payment-options']);
      }
    });
  }

  /**
   * Cargar datos de la payment option específica
   */
  loadPaymentOptionData = async () => {
    this.paymentOption_load = true;
    try {
      // Obtener todas las payment options para encontrar la específica
      const response = await this.paymentOptionsService.getPaymentOptions();
      const paymentOptions = response?.data?.paymentOptions || [];
      
      // Encontrar la payment option específica
      this.paymentOption = paymentOptions.find((option: any) => option.id === this.paymentOptionId);
      
      if (this.paymentOption) {
        // Cargar account payments directamente para esta payment option
        this.getAccountPaymentsByPaymentOption();
        
        // Cargar payment methods solo para "Otros métodos de pagos"
        if (this.isOtrosMetodosDePagos()) {
          this.getPaymentMethodsByPaymentOption();
        }
      } else {
        console.error('Payment option not found');
        this.router.navigate(['/console/payment-options']);
      }
    } catch (error) {
      console.error('Error loading payment option:', error);
      this.router.navigate(['/console/payment-options']);
    } finally {
      this.paymentOption_load = false;
    }
  };

  /**
   * Navegar de vuelta a la lista principal
   */
  goBack = () => {
    this.router.navigate(['/console/payment-options']);
  };

  /**
   * Verificar si la payment option actual es "Otros métodos de pagos"
   */
  isOtrosMetodosDePagos = (): boolean => {
    if (!this.paymentOption) {
      return false;
    }

    // Verificar por nombre o código
    const code = this.paymentOption.code?.toLowerCase() || '';
    return code.toUpperCase().includes('ALL_PAYMENT_METHODS');
          
  };

  /**
   * Obtener account payments filtrados por payment option usando el nuevo endpoint
   */
  getAccountPaymentsByPaymentOption = async () => {
    this.accountPayment_load = true;
    try {
      const response = await this._user.account_payment_by_payment_option(this.paymentOptionId);
      
      if (response && response.data) {
        this.accountPayment = response.data;
        
        // Ordenar por prioridad y estado
        this.accountPayment = this.accountPayment.sort((a: any, b: any) => {
          // Primero por tipo de confirmación (AUTO primero)
        if (a.typeConfirm === 'AUTO' && b.typeConfirm !== 'AUTO') return -1;
        if (b.typeConfirm === 'AUTO' && a.typeConfirm !== 'AUTO') return 1;
          
          // Luego por visibilidad (showClient activos primero)
        if (a.showClient && !b.showClient) return -1;
        if (!a.showClient && b.showClient) return 1;
          
          // Finalmente por prioridad (si está disponible)
          if (a.priority && b.priority) {
            return a.priority - b.priority;
          }
          
        return 0;
      });

        // Extraer tipos únicos de cuenta
        this.extractUniqueAccountTypes();
        this.extractUniqueBanks(); // Extraer bancos únicos
        
        // Aplicar filtros iniciales
        this.applyFilters();
        
        console.log(`Loaded ${this.accountPayment.length} account payments for payment option ${this.paymentOptionId}`);
      } else {
        console.warn('No account payments found for this payment option');
        this.accountPayment = [];
        this.accountPaymentFiltered = [];
      }
    } catch (error) {
      console.error('Error loading account payments:', error);
      this.accountPayment = [];
      this.accountPaymentFiltered = [];
    } finally {
      this.accountPayment_load = false;
    }
  };

  /**
   * Extraer tipos únicos de cuenta para el filtro
   */
  extractUniqueAccountTypes = () => {
    if (!this.accountPayment) return;
    
    const types = this.accountPayment.map((account: any) => ({
      code: account.payment_method?.code || 'unknown',
      name: account.payment_method?.name || 'Desconocido'
    }));
    
    // Eliminar duplicados
    this.uniqueAccountTypes = types.filter((type: any, index: number, self: any[]) => 
      index === self.findIndex((t: any) => t.code === type.code)
    );
  };

  /**
   * Extraer bancos únicos para el filtro
   */
  extractUniqueBanks = () => {
    if (!this.accountPayment) return;
    
    const banks = this.accountPayment
      .filter((account: any) => account.bank?.code) // Solo incluir cuentas con banco
      .map((account: any) => ({
        code: account.bank.code,
        name: account.bank.name || account.bank.code
      }));
    
    // Eliminar duplicados
    this.uniqueBanks = banks.filter((bank: any, index: number, self: any[]) => 
      index === self.findIndex((b: any) => b.code === bank.code)
    );
    
    // Ordenar por nombre
    this.uniqueBanks.sort((a: any, b: any) => a.name.localeCompare(b.name));
  };

  /**
   * Aplicar filtros a las cuentas
   */
  applyFilters = () => {
    if (!this.accountPayment) {
      this.accountPaymentFiltered = [];
      return;
    }

    let filtered = [...this.accountPayment];

    // Filtro por tipo de cuenta
    if (this.filters.accountType !== 'all') {
      filtered = filtered.filter((account: any) => 
        account.payment_method?.code === this.filters.accountType
      );
    }

    // Filtro por banco
    if (this.filters.bank !== 'all') {
      filtered = filtered.filter((account: any) => 
        account.bank?.code === this.filters.bank
      );
    }

    // Filtro por verificación automática
    if (this.filters.automaticVerification !== 'all') {
      if (this.filters.automaticVerification === 'auto') {
        filtered = filtered.filter((account: any) => account.typeConfirm === 'AUTO');
      } else if (this.filters.automaticVerification === 'manual') {
        filtered = filtered.filter((account: any) => account.typeConfirm !== 'AUTO');
      }
    }

    // Filtro por visibilidad al cliente
    if (this.filters.clientVisibility !== 'all') {
      if (this.filters.clientVisibility === 'visible') {
        filtered = filtered.filter((account: any) => account.showClient === true);
      } else if (this.filters.clientVisibility === 'hidden') {
        filtered = filtered.filter((account: any) => account.showClient === false);
      }
    }

    this.accountPaymentFiltered = filtered;
  };

  /**
   * Manejar cambio de filtro
   */
  onFilterChange = (filterType: string, event: Event) => {
    const target = event.target as HTMLSelectElement;
    (this.filters as any)[filterType] = target.value;
    this.applyFilters();
  };

  /**
   * Limpiar todos los filtros
   */
  clearFilters = () => {
    this.filters = {
      accountType: 'all',
      automaticVerification: 'all',
      clientVisibility: 'all',
      bank: 'all'
    };
    this.applyFilters();
  };

  /**
   * Verificar si hay filtros activos
   */
  hasActiveFilters = (): boolean => {
    return this.filters.accountType !== 'all' || 
           this.filters.automaticVerification !== 'all' || 
           this.filters.clientVisibility !== 'all' ||
           this.filters.bank !== 'all';
  };

  // Métodos heredados del sistema anterior (mantenidos para compatibilidad)
  getPaymentMethods = async () => {
    try {
      let resp = await this._user.payment_methods();
      this.paymentMethods = resp?.resp;
      this.paymentMethods = this.paymentMethods.filter((row: any) => row.admin);
      this.paymentMethodsSelected = this.paymentMethods[0]?.code;
    } catch (error) {
      console.log(error);
      this.paymentMethods = [];
    }
  };
  
  getAccountPayment = async () => {
    // Este método se mantiene para compatibilidad, pero ahora usa el nuevo endpoint
    await this.getAccountPaymentsByPaymentOption();
  };

  changeStatusVerification = async (account_payment: string, typeConfirm: string) => {
    await this._user.updateVerificationStatus(account_payment, typeConfirm);
    this.getAccountPaymentsByPaymentOption();
  }

  changeStatus = async (account_payment: string, showClient: boolean) => {
    await this._user.updateAccountStatus(account_payment, showClient);
    this.getAccountPaymentsByPaymentOption();
  }

  /**
   * Cambiar prioridad de una cuenta (subir o bajar)
   */
  changePriority = async (accountId: string, action: 'UP' | 'DOWN') => {
    try {
      const response = await this._user.updateAccountPriority(this.paymentOptionId, accountId, action);
      
      if (response && response.success) {
        // Recargar las cuentas para obtener las prioridades actualizadas
        await this.getAccountPaymentsByPaymentOption();
        console.log(`Priority ${action === 'UP' ? 'increased' : 'decreased'} successfully`);
      } else {
        console.error('Error updating priority:', response?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error changing priority:', error);
    }
  };

  /**
   * Verificar si una cuenta puede subir de prioridad
   */
  canMoveUp = (account: any): boolean => {
    if (!this.accountPaymentFiltered || this.accountPaymentFiltered.length <= 1) {
      return false;
    }
    
    const currentIndex = this.accountPaymentFiltered.findIndex((acc: any) => acc._id === account._id);
    return currentIndex > 0;
  };

  /**
   * Verificar si una cuenta puede bajar de prioridad
   */
  canMoveDown = (account: any): boolean => {
    if (!this.accountPaymentFiltered || this.accountPaymentFiltered.length <= 1) {
      return false;
    }
    
    const currentIndex = this.accountPaymentFiltered.findIndex((acc: any) => acc._id === account._id);
    return currentIndex < this.accountPaymentFiltered.length - 1;
  };

  /**
   * Obtener la posición actual de una cuenta en la lista filtrada
   */
  getAccountPosition = (account: any): number => {
    if (!this.accountPaymentFiltered) return 0;
    const index = this.accountPaymentFiltered.findIndex((acc: any) => acc._id === account._id);
    return index + 1; // Posición basada en 1
  };

  /**
   * Obtener el total de cuentas en la lista filtrada
   */
  getTotalFilteredAccounts = (): number => {
    return this.accountPaymentFiltered?.length || 0;
  };

  /**
   * Obtener el ícono de la payment option
   */
  getPaymentOptionIcon = (): string => {
    if (!this.paymentOption) return '💳';
    
    switch (this.paymentOption.code) {
      case 'BOTON_PAGOS_VENEZUELA':
        return '🏦';
      case 'BOTON_PAGOS_MERCANTIL':
        return '💳';
      case 'ALL_PAYMENT_METHODS':
        return '💰';
      default:
        return '💳';
    }
  };

  /**
   * Verificar si la payment option está activa
   */
  isPaymentOptionActive = (): boolean => {
    return this.paymentOption?.showClient === true;
  };

  /**
   * Obtener el conteo de cuentas activas (basado en datos filtrados)
   */
  getActiveAccountsCount = (): number => {
    if (!this.accountPaymentFiltered) return 0;
    return this.accountPaymentFiltered.filter((account: any) => account.showClient).length;
  };

  /**
   * Obtener el conteo total de cuentas (basado en datos filtrados)
   */
  getTotalAccountsCount = (): number => {
    return this.accountPaymentFiltered?.length || 0;
  };

  /**
   * Obtener el conteo de cuentas automáticas (basado en datos filtrados)
   */
  getAutomaticAccountsCount = (): number => {
    if (!this.accountPaymentFiltered) return 0;
    return this.accountPaymentFiltered.filter((account: any) => account.typeConfirm === 'AUTO').length;
  };

  /**
   * Obtener el conteo de cuentas manuales (basado en datos filtrados)
   */
  getManualAccountsCount = (): number => {
    if (!this.accountPaymentFiltered) return 0;
    return this.accountPaymentFiltered.filter((account: any) => account.typeConfirm !== 'AUTO').length;
  };

  /**
   * Obtener el conteo total de cuentas sin filtros (para mostrar en estadísticas generales)
   */
  getTotalAccountsCountUnfiltered = (): number => {
    return this.accountPayment?.length || 0;
  };

  /**
   * Obtener el conteo de cuentas activas sin filtros
   */
  getActiveAccountsCountUnfiltered = (): number => {
    if (!this.accountPayment) return 0;
    return this.accountPayment.filter((account: any) => account.showClient).length;
  };

  /**
   * Obtener el nombre del tipo de cuenta por código
   */
  getAccountTypeName = (code: string): string => {
    const accountType = this.uniqueAccountTypes.find(type => type.code === code);
    return accountType ? accountType.name : 'Desconocido';
  };

  /**
   * Obtener el nombre del banco por código
   */
  getBankName = (code: string): string => {
    const bank = this.uniqueBanks.find(bank => bank.code === code);
    return bank ? bank.name : 'Desconocido';
  };

  /**
   * Obtener payment methods filtrados por payment option
   */
  getPaymentMethodsByPaymentOption = async () => {
    this.paymentMethods_load = true;
    try {
      const response = await this._user.payment_methods_by_payment_option(this.paymentOptionId);
      
      if (response && response.success) {
        this.paymentMethodsList = response.data || [];
        this.paymentMethodsListFiltered = [...this.paymentMethodsList];
        
        console.log(`Loaded ${this.paymentMethodsList.length} payment methods for payment option ${this.paymentOptionId}`);
      } else {
        console.warn('No payment methods found for this payment option');
        this.paymentMethodsList = [];
        this.paymentMethodsListFiltered = [];
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      this.paymentMethodsList = [];
      this.paymentMethodsListFiltered = [];
    } finally {
      this.paymentMethods_load = false;
    }
  };

  /**
   * Cambiar el estado showClient de un payment method
   */
  changePaymentMethodStatus = async (paymentMethodId: string, showClient: boolean) => {
    try {
      const response = await this._user.updatePaymentMethodStatus(paymentMethodId, showClient);
      
      if (response && response.success) {
        // Recargar los payment methods para obtener el estado actualizado
        await this.getPaymentMethodsByPaymentOption();
        console.log(`Payment method status updated successfully`);
      } else {
        console.error('Error updating payment method status:', response?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error changing payment method status:', error);
    }
  };

  /**
   * Obtener el conteo de payment methods activos
   */
  getActivePaymentMethodsCount = (): number => {
    if (!this.paymentMethodsListFiltered) return 0;
    return this.paymentMethodsListFiltered.filter((pm: any) => pm.showClient).length;
  };

  /**
   * Obtener el conteo total de payment methods
   */
  getTotalPaymentMethodsCount = (): number => {
    return this.paymentMethodsListFiltered?.length || 0;
  };

  /**
   * Verificar si un account_payment está visible pero su payment_method está oculto
   */
  isAccountVisibleButMethodHidden = (account: any): boolean => {
    if (!account.showClient) {
      return false; // Si el account no está visible, no hay problema
    }

    // Solo verificar si estamos en "Otros métodos de pagos"
    if (!this.isOtrosMetodosDePagos()) {
      return false;
    }

    // Buscar el payment_method de este account en la lista
    const paymentMethod = this.paymentMethodsList.find(
      (pm: any) => pm._id === account.payment_method?._id || pm._id === account.payment_method
    );

    // Si el payment method existe y está oculto, retornar true
    return paymentMethod ? !paymentMethod.showClient : false;
  };
}
